import { booleanAttribute, Component, computed, input, Input } from '@angular/core';
import { NgClass, NgStyle } from '@angular/common';
import { TooltipDirective } from '@app/directives/tooltip.directive';

export const PROGRESS_BAR_COLOR_CLASSES = {
  SHADE_0: 'bg-straw',
  ACCENT_SHADE_2: 'bg-navy-700',
};

/**
 * @description A progress bar component that can project content after the progress bar.
 * Note: When isNegative is given(true), the progress bar will be red not taking into consideration the allow background color change
 * isNegative by default is disabled, can be activation by setting it from outside
 */
@Component({
  selector: 'app-progress-bar-projectable',
  imports: [TooltipDirective, NgClass, NgStyle],
  templateUrl: './progress-bar-projectable.component.html',
  styleUrl: './progress-bar-projectable.component.scss',
})
export class ProgressBarProjectableComponent {
  protected readonly Math = Math;

  /** the main percent of the progress bar (bigger than secondaryPercent) */
  percent = input.required<number>();
  /** the secondary percent of the progress bar (smaller than percent) */
  secondaryPercent = input<number>(null);
  /** if false it will not change colors on done or overflow */
  @Input() allowBgColorChange = true;
  /** if true, the progress bar and text will be red regardless of the fillClass or textColor */
  @Input() isNegative = false;

  @Input() text: string;
  @Input() textColor: string; // tailwind class for text before progress bar
  @Input() fillClass: string; // tailwind class
  @Input() secondaryFillClass: string; // tailwind class for secondary fill
  @Input() heightClass = 'h-2'; // tailwind class
  @Input() tooltipPosition: 'right' | 'left' | 'top' | 'bottom' | undefined = undefined;
  @Input() percentWidth: 'default' | 'big' = 'default';
  @Input() biggerPercentText = false;

  hasTooltip = input<boolean, string | boolean>(false, {
    transform: booleanAttribute,
  });

  protected readonly maxWidth = 100;
  protected readonly minWidth = 0;
  protected readonly completePercentage = 100; // percentage when done

  width = computed(() => {
    if (this.percent() >= this.maxWidth) {
      return this.maxWidth;
    } else if (this.percent() <= this.minWidth) {
      if (this.isNegative) {
        return this.maxWidth; // if negative, we want to show a red color
      }
      return this.minWidth;
    }
    return this.percent();
  });

  secondaryWidth = computed(() => {
    if (this.secondaryPercent() >= this.maxWidth) {
      return this.maxWidth;
    } else if (this.secondaryPercent() <= this.minWidth) {
      return this.minWidth;
    }
    return this.secondaryPercent();
  });

  percentDisplay = computed(() => {
    if (this.secondaryPercent()) {
      return this.secondaryPercent();
    }
    return this.percent();
  });
}
